import { IconButton } from "@anna-money/anna-web-ui";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC, ReactElement } from "react";
import { useState } from "react";
import { Form } from "../../../../components/form/form";
import { Input } from "../../../../components/input/input";
import { Toggle } from "../../../../components/toggle/toggle";
import { useFactory } from "../../../../helpers/reactHooks";
import { FacebookIcon, InstagramIcon, LinkedinIcon } from "../../../../icons/icons";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { FormSectionType } from "../../obPaymentPageForm";
import { socialMediaDetailsValidationConfig } from "../../validation";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import styles from "./socialMediaSection.module.scss";

type SocialMediaForm = Pick<
  PaymentPageForm,
  "instagramLink" | "facebookLink" | "linkedinLink" | "switchedOnSocialMedia"
>;

// Both the page form and the modal form are edited through renderForm. Form<T> is invariant in T,
// so describe only the part renderForm uses; method syntax keeps the parameters bivariant.
/* eslint-disable @typescript-eslint/method-signature-style -- property syntax would make Form<PaymentPageForm> not fit */
type SocialMediaFields = {
  getValue<K extends keyof SocialMediaForm>(field: K): SocialMediaForm[K];
  onChange<K extends keyof SocialMediaForm>(field: K, value: SocialMediaForm[K]): void;
  getError(field: string): string | undefined;
};
/* eslint-enable @typescript-eslint/method-signature-style */

export const SocialMedialSection: FC<FormSectionType> = observer(({ form, store }) => {
  const { instagramLink, facebookLink, linkedinLink, switchedOnSocialMedia } = form.values;
  const modalForm = useFactory(
    () =>
      new Form<SocialMediaForm>(
        { instagramLink, facebookLink, linkedinLink, switchedOnSocialMedia },
        socialMediaDetailsValidationConfig,
      ),
  );

  const [isFilled, setFilled] = useState(!!instagramLink || !!facebookLink || !!linkedinLink);
  const [editMode, setEditMode] = useState(!isFilled);

  const handleChangeToggle = (value: boolean, formData: SocialMediaFields): void => {
    formData.onChange("switchedOnSocialMedia", value);
    if (!value) {
      formData.onChange("instagramLink", "");
      formData.onChange("facebookLink", "");
      formData.onChange("linkedinLink", "");
    }
  };

  const renderViewContent = (): ReactElement => (
    <div className={styles.viewSection}>
      <div className={styles.contactDetailsSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionViewTitle}>
            <span className={styles.sectionSubtitle}>Social media</span>
          </div>

          <IconButton
            title="Edit social media"
            icon="actionEdit"
            iconColor="themeGreen"
            size="s"
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>
        <div className={styles.sectionViewContent}>
          {instagramLink && (
            <div className={styles.rowContent}>
              <InstagramIcon height="17" width="17" />
              <span className={styles.text}>{instagramLink}</span>
            </div>
          )}
          {facebookLink && (
            <div className={styles.rowContent}>
              <FacebookIcon height="17" width="17" />
              <span className={styles.text}>{facebookLink}</span>
            </div>
          )}
          {linkedinLink && (
            <div className={styles.rowContent}>
              <LinkedinIcon width="17" height="17" />
              <span className={styles.text}>{linkedinLink}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderForm = (formData: SocialMediaFields): ReactElement => (
    <div className={styles.amountSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <span className={styles.sectionSubtitle}>Social media</span>
          <span className={styles.sectionDescription}>Include your social media links</span>
        </div>
        <Toggle
          label="Social media"
          value={formData.getValue("switchedOnSocialMedia")}
          onChange={(value) => {
            handleChangeToggle(value, formData);
          }}
        />
      </div>

      {formData.getValue("switchedOnSocialMedia") && (
        <>
          <div className={styles.rowContent}>
            <InstagramIcon height="26" width="26" />
            <Input
              className={styles.field}
              id="instagramLink"
              placeholder="Instagram profile"
              aria-label="Instagram"
              autoCapitalize="none"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                formData.onChange("instagramLink", e.target.value);
                store.clearValidationError("instagramLink");
              }}
              value={formData.getValue("instagramLink") ?? undefined}
              error={formData.getError("instagramLink") || store.getValidationError("instagramLink")}
            />
          </div>
          <div className={styles.rowContent}>
            <FacebookIcon height="26" width="26" />
            <Input
              className={styles.field}
              placeholder="Facebook profile"
              aria-label="Facebook"
              id="facebookLink"
              autoCapitalize="none"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                formData.onChange("facebookLink", e.target.value);
                store.clearValidationError("facebookLink");
              }}
              value={formData.getValue("facebookLink") ?? undefined}
              error={formData.getError("facebookLink") || store.getValidationError("facebookLink")}
            />
          </div>
          <div className={styles.rowContent}>
            <LinkedinIcon height="26" width="26" />
            <Input
              className={styles.field}
              placeholder="LinkedIn profile"
              aria-label="LinkedIn"
              autoCapitalize="none"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                formData.onChange("linkedinLink", e.target.value);
                store.clearValidationError("linkedinLink");
              }}
              value={formData.getValue("linkedinLink") ?? undefined}
              error={formData.getError("linkedinLink") || store.getValidationError("linkedinLink")}
            />
          </div>
        </>
      )}
    </div>
  );

  const handleCancelEditModal = (): void => {
    setEditMode(false);
    modalForm.onMultipleChange([
      { field: "linkedinLink", value: form.getValue("linkedinLink") },
      { field: "facebookLink", value: form.getValue("facebookLink") },
      { field: "instagramLink", value: form.getValue("instagramLink") },
      { field: "switchedOnSocialMedia", value: form.getValue("switchedOnSocialMedia") },
    ]);
  };

  const handleSaveEditModal = (): void => {
    if (!modalForm.isValid()) {
      return;
    }

    setEditMode(false);
    if (
      [
        modalForm.getValue("linkedinLink"),
        modalForm.getValue("facebookLink"),
        modalForm.getValue("instagramLink"),
      ].some(Boolean)
    ) {
      setFilled(true);
    } else {
      form.onChange("switchedOnSocialMedia", false);
      setFilled(false);
    }
    form.onMultipleChange([
      { field: "linkedinLink", value: modalForm.getValue("linkedinLink") },
      { field: "facebookLink", value: modalForm.getValue("facebookLink") },
      { field: "instagramLink", value: modalForm.getValue("instagramLink") },
      { field: "switchedOnSocialMedia", value: modalForm.getValue("switchedOnSocialMedia") },
    ]);
  };

  if (store.firstUse || !isFilled) {
    return (
      <div className={classnames(styles.section, styles.withSeparator)}>
        <div>{renderForm(form)}</div>
      </div>
    );
  }

  return (
    <>
      {renderViewContent()}
      <EditFormModalSection open={editMode} onCancel={handleCancelEditModal} onSave={handleSaveEditModal}>
        <div className={styles.section}>
          <div>{renderForm(modalForm)}</div>
        </div>
      </EditFormModalSection>
    </>
  );
});
